import { ActionTree } from 'vuex'
import phrasesApiService from '@/services/phrases'
import { PhrasesMutationTypes } from './mutations'

import type { RootState } from '../../state'
import type { PhrasesState } from './state'
import type {
  PhraseGroup,
  Phrase,
  CreatePhraseGroupRequest,
  UpdatePhraseGroupRequest,
  CreatePhraseRequest,
  UpdatePhraseRequest,
} from '@/models/phrases'

export enum PhrasesAction {
  GetGroups = 'GetGroups',
  CreateGroup = 'CreateGroup',
  UpdateGroup = 'UpdateGroup',
  DeleteGroup = 'DeleteGroup',
  PatchPhrase = 'PatchPhrase',
  GetPhrases = 'GetPhrases',
  CreatePhrase = 'CreatePhrase',
  UpdatePhrase = 'UpdatePhrase',
  DeletePhrase = 'DeletePhrase',
  PublishPhrase = 'PublishPhrase',
  UnpublishPhrase = 'UnpublishPhrase',
}

export type UpdatePhraseGroupActionPayload = {
  id: string
  data: UpdatePhraseGroupRequest
}

export type PatchPhraseActionPayload = {
  groupId: string
  phraseId: string
  data: Partial<Phrase>
}

export type CreatePhraseActionPayload = {
  groupId: string
  data: CreatePhraseRequest
}

export type UpdatePhraseActionPayload = {
  groupId: string
  phraseId: string
  data: UpdatePhraseRequest
}

export type DeletePhraseActionPayload = {
  groupId: string
  phraseId: string
}

export type PublishPhraseActionPayload = {
  groupId: string
  phraseId: string
}

export type UnpublishPhraseActionPayload = {
  groupId: string
  phraseId: string
}

export const actions: ActionTree<PhrasesState, RootState> = {
  async [PhrasesAction.GetGroups](context) {
    const response = await phrasesApiService.getPhrasesGroups()

    context.commit(PhrasesMutationTypes.SetGroups, response.data)

    return response
  },

  async [PhrasesAction.CreateGroup](context, payload: CreatePhraseGroupRequest) {
    const response = await phrasesApiService.createPhraseGroup(payload)

    const groups = context.getters.getPhrasesGroups as PhraseGroup[]
    const newGroups = [...groups, response.data]
    context.commit(PhrasesMutationTypes.SetGroups, newGroups)

    return response
  },

  async [PhrasesAction.UpdateGroup](context, payload: UpdatePhraseGroupActionPayload) {
    const { id, data } = payload
    const response = await phrasesApiService.updatePhraseGroup(id, data)

    const groups = [...context.getters.getPhrasesGroups] as PhraseGroup[]
    const currentGroupIndex = groups.findIndex(g => g.id === id)
    if (currentGroupIndex >= 0) {
      groups[currentGroupIndex] = {
        ...groups[currentGroupIndex],
        ...data,
      }

      context.commit(PhrasesMutationTypes.SetGroups, groups)
    }

    return response
  },

  async [PhrasesAction.DeleteGroup](context, payload: string) {
    const response = await phrasesApiService.deletePhraseGroup(payload)

    const groups = context.getters.getPhrasesGroups as PhraseGroup[]
    const newGroups = groups.filter(g => g.id !== payload)
    context.commit(PhrasesMutationTypes.SetGroups, newGroups)

    return response
  },

  [PhrasesAction.PatchPhrase](context, payload: PatchPhraseActionPayload) {
    const { groupId, phraseId, data } = payload

    const phrases = context.getters.getPhrases as Record<string, Phrase[] | undefined>
    const groupPhrases = [...(phrases[groupId] || [])]

    const phraseIndex = groupPhrases.findIndex(p => p.id === phraseId)

    if (phraseIndex >= 0) {
      groupPhrases[phraseIndex] = {
        ...groupPhrases[phraseIndex],
        ...data,
      }

      const newPhrases = {
        ...phrases,
        [groupId]: groupPhrases
      }
      context.commit(PhrasesMutationTypes.SetPhrases, newPhrases)
    }
  },

  async [PhrasesAction.GetPhrases](context, payload: string) {
    const phrases = context.getters.getPhrases as Record<string, Phrase[] | undefined>

    if (phrases[payload]) {
      return phrases[payload]
    }

    const response = await phrasesApiService.getPhrasesByGroup(payload)

    const newPhrases = {
      ...phrases,
      [payload]: response.data
    }

    context.commit(PhrasesMutationTypes.SetPhrases, newPhrases)

    return response
  },

  async [PhrasesAction.CreatePhrase](context, payload: CreatePhraseActionPayload) {
    const { groupId, data } = payload 
    const response = await phrasesApiService.createPhrase(groupId, data)

    const phrases = context.getters.getPhrases as Record<string, Phrase[] | undefined>

    const newPhrases = {
      ...phrases,
      [groupId]: [...(phrases[groupId] || []), response.data]
    }
    context.commit(PhrasesMutationTypes.SetPhrases, newPhrases)

    return response
  },

  async [PhrasesAction.UpdatePhrase](context, payload: UpdatePhraseActionPayload) {
    const { groupId, phraseId, data } = payload 
    const response = await phrasesApiService.updatePhrase(groupId, phraseId, data)

    const patchData: PatchPhraseActionPayload = {
      groupId,
      phraseId,
      data,
    }
    context.dispatch(PhrasesAction.PatchPhrase, patchData)

    return response
  },

  async [PhrasesAction.DeletePhrase](context, payload: DeletePhraseActionPayload) {
    const { groupId, phraseId } = payload 
    const response = await phrasesApiService.deletePhrase(groupId, phraseId)

    const phrases = context.getters.getPhrases as Record<string, Phrase[] | undefined>

    const newPhrases = {
      ...phrases,
      [groupId]: (phrases[groupId] || []).filter(p => p.id !== phraseId)
    }
    context.commit(PhrasesMutationTypes.SetPhrases, newPhrases)

    return response
  },

  async [PhrasesAction.PublishPhrase](context, payload: PublishPhraseActionPayload) {
    const { groupId, phraseId } = payload 
    const response = await phrasesApiService.publishPhrase(groupId, phraseId)

    const patchData: PatchPhraseActionPayload = {
      groupId,
      phraseId,
      data: { published: true }
    }
    context.dispatch(PhrasesAction.PatchPhrase, patchData)

    return response
  },

  async [PhrasesAction.UnpublishPhrase](context, payload: UnpublishPhraseActionPayload) {
    const { groupId, phraseId } = payload 
    const response = await phrasesApiService.unpublishPhrase(groupId, phraseId)

    const patchData: PatchPhraseActionPayload = {
      groupId,
      phraseId,
      data: { published: false }
    }
    context.dispatch(PhrasesAction.PatchPhrase, patchData)

    return response
  },
}
